<script setup>
import PageGuidance from '@/components/guidance/PageGuidance.vue'
import PageHeader from '@/components/shared/PageHeader.vue'
import StatusChip from '@/components/shared/StatusChip.vue'
import { useClientsStore } from '@/stores/clients'

definePage({
  meta: {
    navActiveLink: 'clients',
  },
})

const store = useClientsStore()
const router = useRouter()
const { t } = useI18n()

const inviteDialog = ref(false)
const createDialog = ref(false)
const formError = ref('')
const formSuccess = ref('')

const inviteForm = ref({ email: '', name: '' })

const createForm = ref({
  email: '',
  name: '',
  phone: '',
  injuries: '',
})

const route = useRoute()

onMounted(() => {
  const relationshipState = route.query.relationship_lifecycle_state
  if (typeof relationshipState === 'string' && relationshipState)
    store.filters.relationship_lifecycle_state = relationshipState

  store.fetchClients().catch(() => null)
})

const applyFilters = () => {
  store.fetchClients().catch(() => null)
}

const openClient = id => {
  router.push({ name: 'clients-id', params: { id } })
}

const submitInvite = async () => {
  formError.value = ''
  formSuccess.value = ''
  try {
    const data = await store.inviteClient(inviteForm.value)

    formSuccess.value = data.message || t('clients.invitationProcessed')
    inviteDialog.value = false
    inviteForm.value = { email: '', name: '' }
    await store.fetchClients()
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}

const submitCreate = async () => {
  formError.value = ''
  formSuccess.value = ''
  try {
    const client = await store.createClient(createForm.value)

    createDialog.value = false
    createForm.value = { email: '', name: '', phone: '', injuries: '' }
    await router.push({ name: 'clients-id', params: { id: client.id } })
  }
  catch (err) {
    formError.value = err.payload?.errors?.join(' ') || err.message
  }
}
</script>

<template>
  <div>
    <PageHeader
      :title="$t('clients.title')"
      :subtitle="$t('clients.subtitle')"
    >
      <template #actions>
        <VBtn
          variant="tonal"
          data-tour="clients-add"
          :disabled="store.saving"
          @click="createDialog = true"
        >
          {{ $t('clients.addWithoutInvite') }}
        </VBtn>
        <VBtn
          color="primary"
          data-tour="clients-invite"
          :disabled="store.saving"
          @click="inviteDialog = true"
        >
          {{ $t('clients.inviteClient') }}
        </VBtn>
        <PageGuidance />
      </template>
    </PageHeader>

    <VAlert
      v-if="formSuccess"
      type="success"
      variant="tonal"
      class="mb-4"
      role="status"
    >
      {{ formSuccess }}
    </VAlert>

    <VAlert
      v-if="store.error"
      type="error"
      variant="tonal"
      class="mb-4"
      role="alert"
    >
      {{ store.error }}
    </VAlert>

    <VCard
      flat
      border
      class="mb-4"
    >
      <VCardText class="d-flex flex-wrap gap-4">
        <VTextField
          v-model="store.filters.q"
          :label="$t('clients.searchLabel')"
          density="comfortable"
          hide-details
          style="max-inline-size: 20rem;"
          @keyup.enter="applyFilters"
        />
        <VSelect
          v-model="store.filters.lifecycle_state"
          :label="$t('clients.statusLabel')"
          density="comfortable"
          hide-details
          clearable
          style="max-inline-size: 14rem;"
          :items="[
            { title: $t('clients.statusAll'), value: '' },
            { title: $t('clients.status.prospective'), value: 'prospective' },
            { title: $t('clients.status.onboarded'), value: 'onboarded' },
            { title: $t('clients.status.active'), value: 'active' },
            { title: $t('clients.status.inactive'), value: 'inactive' },
          ]"
          @update:model-value="applyFilters"
        />
        <VSelect
          v-model="store.filters.relationship_lifecycle_state"
          :label="$t('clients.relationshipLabel')"
          density="comfortable"
          hide-details
          clearable
          style="max-inline-size: 14rem;"
          :items="[
            { title: $t('clients.relationshipAll'), value: '' },
            { title: $t('clients.relationship.prospect'), value: 'prospect' },
            { title: $t('clients.relationship.active'), value: 'active' },
            { title: $t('clients.relationship.paused'), value: 'paused' },
            { title: $t('clients.relationship.ended'), value: 'ended' },
          ]"
          @update:model-value="applyFilters"
        />
        <VBtn
          variant="tonal"
          :loading="store.loading"
          @click="applyFilters"
        >
          {{ $t('clients.search') }}
        </VBtn>
      </VCardText>
    </VCard>

    <div
      v-if="store.loading"
      class="text-medium-emphasis"
      role="status"
    >
      {{ $t('clients.loading') }}
    </div>

    <div
      v-else
      data-tour="clients-list"
    >
      <VAlert
        v-if="store.isEmpty"
        type="info"
        variant="tonal"
        role="status"
      >
        {{ $t('clients.empty') }}
      </VAlert>

      <VList
        v-else
        lines="two"
        border
        rounded
      >
        <VListItem
          v-for="client in store.clients"
          :key="client.id"
          :title="client.name || client.email"
          :subtitle="client.email"
          @click="openClient(client.id)"
        >
          <template #append>
            <div class="d-flex flex-wrap ga-2 justify-end">
              <StatusChip
                domain="client"
                :state="client.lifecycle_state"
                show-prefix
              />
              <StatusChip
                v-if="client.coaching_relationship?.lifecycle_state"
                domain="relationship"
                :state="client.coaching_relationship.lifecycle_state"
                show-prefix
              />
              <StatusChip
                v-if="client.open_subscription?.lifecycle_state"
                domain="subscription"
                :state="client.open_subscription.lifecycle_state"
                show-prefix
              />
              <StatusChip
                v-if="client.outstanding_invoice?.lifecycle_state"
                domain="invoice"
                :state="client.outstanding_invoice.lifecycle_state"
              />
              <VChip
                v-if="client.active_session_package"
                size="small"
                :color="client.active_session_package.nearing_depletion || client.active_session_package.nearing_expiry ? 'warning' : 'success'"
                variant="tonal"
                label
              >
                <span class="visually-hidden">{{ $t('sessionPackages.statusPrefix') }} </span>
                {{ $t('sessionPackages.remainingShort', { count: client.active_session_package.sessions_remaining }) }}
              </VChip>
            </div>
          </template>
        </VListItem>
      </VList>
    </div>

    <VDialog
      v-model="inviteDialog"
      max-width="480"
    >
      <VCard :title="$t('clients.inviteDialog.title')">
        <VCardText>
          <p class="text-body-2 mb-4">
            {{ $t('clients.inviteDialog.description') }}
          </p>
          <VAlert
            v-if="formError"
            type="error"
            variant="tonal"
            class="mb-4"
            role="alert"
          >
            {{ formError }}
          </VAlert>
          <VTextField
            v-model="inviteForm.email"
            :label="$t('common.email')"
            type="email"
            class="mb-3"
          />
          <VTextField
            v-model="inviteForm.name"
            :label="$t('clients.inviteDialog.nameOptionalLabel')"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="inviteDialog = false"
          >
            {{ $t('common.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="store.saving"
            @click="submitInvite"
          >
            {{ $t('clients.inviteDialog.submit') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <VDialog
      v-model="createDialog"
      max-width="520"
    >
      <VCard :title="$t('clients.createDialog.title')">
        <VCardText>
          <p class="text-body-2 mb-4">
            {{ $t('clients.createDialog.description') }}
          </p>
          <VAlert
            v-if="formError"
            type="error"
            variant="tonal"
            class="mb-4"
            role="alert"
          >
            {{ formError }}
          </VAlert>
          <VTextField
            v-model="createForm.email"
            :label="$t('common.email')"
            type="email"
            class="mb-3"
          />
          <VTextField
            v-model="createForm.name"
            :label="$t('common.name')"
            class="mb-3"
          />
          <VTextField
            v-model="createForm.phone"
            :label="$t('common.phone')"
            class="mb-3"
          />
          <VTextarea
            v-model="createForm.injuries"
            :label="$t('clients.createDialog.injuriesLabel')"
            rows="2"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="createDialog = false"
          >
            {{ $t('common.cancel') }}
          </VBtn>
          <VBtn
            color="primary"
            :loading="store.saving"
            @click="submitCreate"
          >
            {{ $t('clients.createDialog.submit') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>

<style scoped>
.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
